import { Tooltip, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  differenceInBusinessDays,
  differenceInMilliseconds,
  isWeekend,
  startOfWeek,
} from "date-fns";
import { ScheduleEntry, useScheduleContext } from "./ScheduleContext";

export type ScheduleItemProps = {
  entry: ScheduleEntry;
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  state?: "tentative" | "confirmed" | "cancelled";
  tooltip?: React.ReactNode;
};

const ONE_DAY_MS = 86_400_000;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;
const BUSINESS_HOUR_START = 8;
const BUSINESS_HOUR_END = 17;
const BUSINESS_HOURS = BUSINESS_HOUR_END - BUSINESS_HOUR_START;

/**
 * Maps a date to a linear position (in milliseconds from viewFromDate)
 * in the workweeks timeline. Business hours (08:00–17:00) on business days
 * (Mon–Fri) are distributed evenly across each week's column width.
 *
 * Times outside business hours are clamped.
 * Weekend dates are mapped to Friday 17:00 (end of the week column).
 */
function dateToWorkweekPosition(date: Date, viewFromDate: Date): number {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekOffset = differenceInMilliseconds(weekStart, viewFromDate);

  // Day index: 0=Mon through 6=Sun
  const dayIndex = (date.getDay() + 6) % 7;

  // Weekend → end of Friday (position = 100% through the week column)
  if (dayIndex >= 5) {
    return weekOffset + ONE_WEEK_MS;
  }

  // Clamp time to business hours
  const fractionalHours = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
  const clampedHours = Math.max(BUSINESS_HOUR_START, Math.min(BUSINESS_HOUR_END, fractionalHours));
  const hourFraction = (clampedHours - BUSINESS_HOUR_START) / BUSINESS_HOURS;

  // Each workday gets 1/5 of the week column width
  const positionInWeek = (dayIndex + hourFraction) / 5;
  return weekOffset + positionInWeek * ONE_WEEK_MS;
}

const ScheduleItemRoot = styled("div")({
  "&.ScheduleItem-root": {
    position: "absolute",
    top: "calc(50% - 10px)",
    height: 20,
    border: "1px solid var(--fg-color)",
    boxSizing: "border-box",
    borderRadius: 4,
    borderColor: "var(--fg-color)",
    backgroundColor: "var(--bg-color)",
    fontWeight: "bold",
    containerType: "inline-size",
  },

  "&.ScheduleItem-confirmed": {
    color: "white",
  },

  "&.ScheduleItem-cancelled": {
    backgroundSize: "10px 10px",
    backgroundImage: `
      linear-gradient(-45deg,
        var(--fg-color), var(--fg-color) 3%,
        transparent 3%, transparent 50%,
        var(--fg-color) 50%, var(--fg-color) 53%,
        transparent 53%, transparent)
    `,
  },

  "&.ScheduleItem-selected": {
    borderColor: "white",
    boxShadow: "0 0 0 2px black",
  },

  "&.ScheduleItem-showDayNumbers": {
    "&::before": {
      content: "var(--start-day)",
      position: "absolute",
      top: "calc(50% - 10px)",
      left: "50%",
      transform: "translateX(-50%)",
      paddingInline: "4px",
    },

    "&:not(.ScheduleItem-multiDay)::before": {
      "@container (max-width: 1em)": {
        display: "none",
      },
    },

    "&.ScheduleItem-multiDay::before": {
      left: 0,
      transform: "none",

      "@container (max-width: 2em)": {
        display: "none",
      },
    },

    "&.ScheduleItem-multiDay::after": {
      content: "var(--end-day)",
      position: "absolute",
      top: "calc(50% - 10px)",
      right: 0,
      paddingInline: "4px",

      "@container (max-width: 2em)": {
        display: "none",
      },
    },
  },
});

const ScheduleItem: React.FC<ScheduleItemProps> = (props) => {
  const { entry, color = "primary", state = "tentative", tooltip } = props;
  const {
    fromDate,
    totalDuration,
    scale,
    enableSelection,
    selectedEntries,
    showDayNumbers,
    toggleSelection,
  } = useScheduleContext();
  const theme = useTheme();

  const adjustToWorkweek = scale === "WORKWEEKS";

  const entryFrom = entry.from;
  const entryTo = entry.to;

  const workDays =
    differenceInBusinessDays(entryTo, entryFrom) +
    // If last day is workday, add one to include it in the duration
    (isWeekend(entryTo) ? 0 : 1);

  const startTime = adjustToWorkweek
    ? dateToWorkweekPosition(entryFrom, fromDate)
    : differenceInMilliseconds(entryFrom, fromDate);

  const duration = adjustToWorkweek
    ? dateToWorkweekPosition(entryTo, fromDate) - startTime
    : differenceInMilliseconds(entryTo, entryFrom);

  const startDay = entryFrom.getDay();
  const endDay = adjustToWorkweek ? Math.min(entryTo.getDay() || 7, 5) : entryTo.getDay() || 7;

  const fgColor = theme.palette[color].main;
  const bgColor =
    state === "confirmed" ? theme.palette[color].light : theme.palette.background[color];

  const classNames = [
    "ScheduleItem-root",
    state === "confirmed" && "ScheduleItem-confirmed",
    state === "cancelled" && "ScheduleItem-cancelled",
    enableSelection && selectedEntries.includes(entry.id) && "ScheduleItem-selected",
    showDayNumbers && "ScheduleItem-showDayNumbers",
    workDays > 1 && "ScheduleItem-multiDay",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tooltip key={entry.id} followCursor title={tooltip}>
      <ScheduleItemRoot
        onClick={() => enableSelection && toggleSelection(entry.id)}
        className={classNames}
        style={
          {
            "--fg-color": fgColor,
            "--bg-color": bgColor,
            "--start-day": `"${startDay}"`,
            "--end-day": `"${endDay}"`,
            left: `${(startTime / totalDuration) * 100}%`,
            width: `${(duration / totalDuration) * 100}%`,
          } as React.CSSProperties
        }
      />
    </Tooltip>
  );
};

export default ScheduleItem;
